import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonIconText, SearchBox } from '@/components';
import { EmployeesTable, SendInviteDialog } from './components';
import { useEmployeeSidePanel } from '@/features/employee-sidepanel';
import {
  useEmployeeList,
  useJoinRequests,
  useSendInviteLink,
  useUpdatePrimaryContact,
} from './hooks';
import type { Employee, EmployeeListItem, JoinRequestItem } from './types';
import Pagination from '@/components/tables/Pagination';

interface EmployeesSectionProps {
  companyId: string;
}

export function EmployeesSection({ companyId }: EmployeesSectionProps) {
  const { t } = useTranslation();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmployee, setInviteEmployee] = useState<Employee | null>(null);

  const {
    searchInput,
    setSearchInput,
    page,
    setPage,
    pageSize,
    setPageSize,
    selectedIds,
    setSelectedIds,
    employees,
    total,
    isLoading,
    isFetching,
  } = useEmployeeList({ companyId });
  const sidePanel = useEmployeeSidePanel();

  const sendInvite = useSendInviteLink(companyId);
  const updatePrimary = useUpdatePrimaryContact(companyId);


  const { data: joinRequestsData } = useJoinRequests(companyId);

  const joinRequestsAsEmployees: EmployeeListItem[] = useMemo(() => {
    if (!joinRequestsData?.data) return [];
    return joinRequestsData.data.map((item: JoinRequestItem): EmployeeListItem => ({
      id: item.id,
      isNew: true,
      user: {
        userId: item.userId,
        username: item.userName,
        userEmail: item.userEmail,
        managedByCompany: false,
      },
      joinRequest: item
    }));
  }, [joinRequestsData]);

  const employeesWithJoinRequest: EmployeeListItem[] = useMemo(() => {
    const searchLower = searchInput.toString().toLowerCase();

    const filteredJoinRequests = joinRequestsAsEmployees.filter((employee) => {
      const name = employee.user?.username || '';
      return name.toLowerCase().includes(searchLower);
    });

    console.log('filteredJoinRequests', filteredJoinRequests);
    console.log('employees', employees)

    return [...filteredJoinRequests, ...employees];
  }, [employees, searchInput, joinRequestsAsEmployees]);


  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setSelectedIds(new Set());
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
    setSelectedIds(new Set());
  }, []);

  const handleRowClick = useCallback((employee: EmployeeListItem) => {
    sidePanel.open({ type: 'edit', employee }, companyId);
  }, [sidePanel, companyId]);


  const handleAddEmployee = useCallback(() => {
    sidePanel.open({ type: 'create' }, companyId);
  }, [sidePanel, companyId]);

  const handleAcceptRequest = useCallback(
    (employee: EmployeeListItem) => {
      if (employee.user && employee.joinRequest) {
        sidePanel.open(
          {
            type: 'acceptJoinRequest',
            joinRequest: {
              id: employee.id,
              userName: employee.user.username,
              userEmail: employee.user.userEmail,
            },
          },
          companyId
        );
      }
    },
    [sidePanel, companyId]
  );

  const handleOpenInviteDialog = useCallback(
    (employee: Employee) => {
      setInviteEmployee(employee);
      setInviteDialogOpen(true);
    },
    []
  );

  const handleCloseInviteDialog = useCallback(() => {
    setInviteDialogOpen(false);
    setInviteEmployee(null);
  }, []);

  const handleSendInvite = useCallback(
    (email: string) => {
      if (inviteEmployee?.contact?.id) {
        sendInvite.mutate(
          { contactId: inviteEmployee.contact.id, email },
          {
            onSuccess: () => {
              handleCloseInviteDialog();
            },
          }
        );
      }
    },
    [inviteEmployee, sendInvite, handleCloseInviteDialog]
  );

  const handleTogglePrimary = useCallback(
    (employee: Employee, isPrimary: boolean) => {
      if (employee.contact?.id) {
        updatePrimary.mutate({ contactId: employee.contact.id, isPrimary });
      }
    },
    [updatePrimary]
  );

  return (
    <div className="space-y-2 w-full grow min-h-0 h-full flex flex-col">

      <div className="flex flex-col sm:flex-row gap-2">
        <SearchBox
          className="sm:grow"
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('employees.search.placeholder')}
          ariaLabel={t('employees.search.label')}
          clearAriaLabel={t('employees.search.clear')}
        />
        <ButtonIconText
          iconName="plus"
          onClick={handleAddEmployee}
          className='sm:content-center'
          aria-label={t('employees.addButton')}
        >
          {t('employees.addButton')}
        </ButtonIconText>
      </div>


      <div className="relative grow min-h-0">
        {isFetching && !isLoading && (
          <div className="absolute top-2 right-2 z-10">
            <div className="animate-spin size-5 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        )}
        <EmployeesTable
          employees={employeesWithJoinRequest}
          isLoading={isLoading}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onRowClick={handleRowClick}
          onAcceptRequest={handleAcceptRequest}
          onSendInvite={handleOpenInviteDialog}
          onTogglePrimary={handleTogglePrimary}
        />
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <SendInviteDialog
        isOpen={inviteDialogOpen}
        onClose={handleCloseInviteDialog}
        onSendInvite={handleSendInvite}
        isLoading={sendInvite.isPending}
        defaultEmail={inviteEmployee?.contact?.email || ''}
      />
    </div>
  );
}
